// src/utils/continuityReportPdf.js
// Rapport de continuité (galeries, archives, estates) : document préparatoire.
// Distingue les ensembles et leur nature, le statut patrimonial des œuvres, les informations à clarifier,
// les mouvements et expositions, les accès accordés et les démarches à prévoir.
// ARTICHAUT structure l'information ; il ne qualifie rien juridiquement et ne remplace ni l'expert ni le notaire.
// Langues : fr (défaut), en, de — paramètre `lang`.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { BRAND_UPPER } from "../lib/rebrand";

const LANGS = ["fr", "en", "de"];
const pickLang = (lang) => {
  const l = String(lang || "fr").slice(0, 2).toLowerCase();
  return LANGS.includes(l) ? l : "fr";
};
const LOCALE = { fr: "fr-FR", en: "en-GB", de: "de-DE" };

const T = {
  fr: {
    nature: { personal_collection: "Collection personnelle", company_collection: "Collection de société", gallery_archive: "Archives de galerie", artist_estate: "Estate d'artiste" },
    context: { succession: "Succession", sale: "Cession", donation: "Donation", closure: "Fermeture", continuity: "Continuité d'activité" },
    heritage: { gallery_stock: "Stock marchand", personal: "Propriété personnelle", company: "Propriété de la société", deposit: "Dépôt", consignment: "Consignation", entrusted: "Œuvre confiée", to_clarify: "À clarifier" },
    event: { exhibition: "Exposition", loan: "Prêt", location_change: "Changement de lieu", transport: "Transport", collection_entry: "Entrée en collection", sale_mandate: "Mandat de vente", sale: "Vente", donation: "Don", deaccession: "Sortie de collection" },
    checks: { status: "Statut patrimonial non renseigné ou à clarifier", owner: "Propriétaire à vérifier", provenance: "Provenance non renseignée", proof: "Aucune pièce justificative (facture ou document)" },
    title: "RAPPORT DE CONTINUITÉ — DOCUMENT PRÉPARATOIRE",
    fileFallback: "Dossier",
    issuedOn: (d) => `Établi le ${d}`,
    summary: ["Ensembles distingués", "Œuvres documentées", "Œuvres avec au moins un document", "Propriétaire confirmé (non signalé « à vérifier »)", "Points à clarifier"],
    s2: "Ensembles et nature",
    s2Head: ["Ensemble", "Nature", "Contexte", "Œuvres", "Statuts patrimoniaux"],
    untitled: "(sans titre)", notQualified: "Non qualifié", notSpecifiedF: "Non précisée", notSpecifiedM: "Non précisé",
    s2Note: "Le stock marchand en cours reste dans l'outil de gestion de la galerie ; il n'est pas qualifié ici sauf s'il a été importé.",
    s3: "Points à clarifier",
    s3Head: ["Contrôle", "Œuvres concernées", "Exemples"],
    s4: "Chronologie : expositions et mouvements (extrait)",
    s4Head: ["Date", "Œuvre", "Événement", "Détail"],
    noEvent: "Aucun événement enregistré",
    s5: "Accès accordés",
    s5Head: ["Personne ou destinataire", "Droit", "Périmètre", "Expire le"],
    level: { read: "Lecture", read_edit: "Lecture et modification" },
    user: "Utilisateur", noLimit: "sans limite", shareLink: "Lien de partage", readLink: "Lecture (lien)", sharedDossier: "Dossier partagé", noAccess: "Aucun accès accordé",
    s5Note: "Chaque accès est révocable à tout moment ; les consultations par lien sont journalisées.",
    s6: "Démarches à prévoir",
    steps: {
      status: (n) => `Qualifier le statut patrimonial de ${n} œuvre(s) (stock, propriété personnelle, société, dépôt, consignation…).`,
      owner: (n) => `Faire confirmer par un professionnel du droit le propriétaire de ${n} œuvre(s) signalée(s) « à vérifier ».`,
      provenance: (n) => `Rassembler la provenance de ${n} œuvre(s).`,
      proof: (n) => `Retrouver une facture ou un document pour ${n} œuvre(s).`,
      none: "Aucune démarche particulière n'est signalée par les contrôles automatiques.",
    },
    foot: (brand, date) =>
      `Document préparatoire établi via ${brand} le ${date}. Il reprend les informations renseignées à cette date ; il ne constitue ni une expertise, ni un conseil juridique ou fiscal, et ne qualifie pas la propriété des œuvres. ${brand} structure l'information ; les professionnels habilités valident ce qui relève de leur compétence.`,
    fileSlug: "rapport-continuite",
  },
  en: {
    nature: { personal_collection: "Personal collection", company_collection: "Company collection", gallery_archive: "Gallery archives", artist_estate: "Artist estate" },
    context: { succession: "Succession", sale: "Sale of the business", donation: "Donation", closure: "Closure", continuity: "Business continuity" },
    heritage: { gallery_stock: "Trade stock", personal: "Personal property", company: "Company property", deposit: "Deposit", consignment: "Consignment", entrusted: "Entrusted work", to_clarify: "To clarify" },
    event: { exhibition: "Exhibition", loan: "Loan", location_change: "Change of location", transport: "Transport", collection_entry: "Entry into the collection", sale_mandate: "Sale mandate", sale: "Sale", donation: "Donation", deaccession: "Deaccession" },
    checks: { status: "Estate status missing or to be clarified", owner: "Owner to be verified", provenance: "Provenance not provided", proof: "No supporting document (invoice or file)" },
    title: "CONTINUITY REPORT — PREPARATORY DOCUMENT",
    fileFallback: "File",
    issuedOn: (d) => `Issued on ${d}`,
    summary: ["Groups distinguished", "Works documented", "Works with at least one document", "Owner confirmed (not flagged “to verify”)", "Points to clarify"],
    s2: "Groups and their nature",
    s2Head: ["Group", "Nature", "Context", "Works", "Estate statuses"],
    untitled: "(untitled)", notQualified: "Not qualified", notSpecifiedF: "Not specified", notSpecifiedM: "Not specified",
    s2Note: "Current trade stock stays in the gallery's management tool; it is not qualified here unless it has been imported.",
    s3: "Points to clarify",
    s3Head: ["Check", "Works concerned", "Examples"],
    s4: "Timeline: exhibitions and movements (extract)",
    s4Head: ["Date", "Work", "Event", "Detail"],
    noEvent: "No event recorded",
    s5: "Access granted",
    s5Head: ["Person or recipient", "Right", "Scope", "Expires on"],
    level: { read: "Read", read_edit: "Read and edit" },
    user: "User", noLimit: "no limit", shareLink: "Share link", readLink: "Read (link)", sharedDossier: "Shared file", noAccess: "No access granted",
    s5Note: "Each access can be revoked at any time; views through a link are logged.",
    s6: "Steps to plan",
    steps: {
      status: (n) => `Qualify the estate status of ${n} work(s) (stock, personal property, company, deposit, consignment…).`,
      owner: (n) => `Have a legal professional confirm the owner of ${n} work(s) flagged “to verify”.`,
      provenance: (n) => `Gather the provenance of ${n} work(s).`,
      proof: (n) => `Find an invoice or document for ${n} work(s).`,
      none: "The automatic checks flag no particular step.",
    },
    foot: (brand, date) =>
      `Preparatory document produced with ${brand} on ${date}. It reflects the information entered at that date; it is neither an appraisal nor legal or tax advice, and it does not qualify the ownership of the works. ${brand} organises the information; the qualified professionals validate whatever falls within their competence.`,
    fileSlug: "continuity-report",
  },
  de: {
    nature: { personal_collection: "Persönliche Sammlung", company_collection: "Unternehmenssammlung", gallery_archive: "Galeriearchiv", artist_estate: "Künstlernachlass" },
    context: { succession: "Nachfolge", sale: "Verkauf", donation: "Schenkung", closure: "Schließung", continuity: "Fortführung der Tätigkeit" },
    heritage: { gallery_stock: "Handelsbestand", personal: "Privateigentum", company: "Eigentum der Gesellschaft", deposit: "Leihgabe", consignment: "Kommission", entrusted: "Anvertrautes Werk", to_clarify: "Zu klären" },
    event: { exhibition: "Ausstellung", loan: "Leihgabe", location_change: "Standortwechsel", transport: "Transport", collection_entry: "Aufnahme in die Sammlung", sale_mandate: "Verkaufsauftrag", sale: "Verkauf", donation: "Schenkung", deaccession: "Abgang aus der Sammlung" },
    checks: { status: "Vermögensstatus fehlt oder ist zu klären", owner: "Eigentümer zu prüfen", provenance: "Provenienz nicht angegeben", proof: "Kein Beleg (Rechnung oder Dokument)" },
    title: "KONTINUITÄTSBERICHT — VORBEREITENDES DOKUMENT",
    fileFallback: "Akte",
    issuedOn: (d) => `Erstellt am ${d}`,
    summary: ["Unterschiedene Bestände", "Dokumentierte Werke", "Werke mit mindestens einem Dokument", "Eigentümer bestätigt (nicht als „zu prüfen“ markiert)", "Zu klärende Punkte"],
    s2: "Bestände und ihre Art",
    s2Head: ["Bestand", "Art", "Kontext", "Werke", "Vermögensstatus"],
    untitled: "(ohne Titel)", notQualified: "Nicht eingestuft", notSpecifiedF: "Nicht angegeben", notSpecifiedM: "Nicht angegeben",
    s2Note: "Der laufende Handelsbestand bleibt im Verwaltungsprogramm der Galerie; er wird hier nicht eingestuft, sofern er nicht importiert wurde.",
    s3: "Zu klärende Punkte",
    s3Head: ["Prüfung", "Betroffene Werke", "Beispiele"],
    s4: "Chronologie: Ausstellungen und Bewegungen (Auszug)",
    s4Head: ["Datum", "Werk", "Ereignis", "Detail"],
    noEvent: "Kein Ereignis erfasst",
    s5: "Gewährte Zugänge",
    s5Head: ["Person oder Empfänger", "Recht", "Umfang", "Läuft ab am"],
    level: { read: "Lesen", read_edit: "Lesen und Bearbeiten" },
    user: "Nutzer", noLimit: "unbefristet", shareLink: "Freigabelink", readLink: "Lesen (Link)", sharedDossier: "Freigegebene Akte", noAccess: "Kein Zugang gewährt",
    s5Note: "Jeder Zugang kann jederzeit widerrufen werden; Aufrufe über einen Link werden protokolliert.",
    s6: "Vorzusehende Schritte",
    steps: {
      status: (n) => `Den Vermögensstatus von ${n} Werk(en) einstufen (Bestand, Privateigentum, Gesellschaft, Leihgabe, Kommission…).`,
      owner: (n) => `Den Eigentümer von ${n} als „zu prüfen“ markierten Werk(en) von einer rechtskundigen Person bestätigen lassen.`,
      provenance: (n) => `Die Provenienz von ${n} Werk(en) zusammentragen.`,
      proof: (n) => `Für ${n} Werk(e) eine Rechnung oder ein Dokument finden.`,
      none: "Die automatischen Prüfungen weisen auf keinen besonderen Schritt hin.",
    },
    foot: (brand, date) =>
      `Vorbereitendes Dokument, erstellt mit ${brand} am ${date}. Es gibt die zu diesem Datum erfassten Angaben wieder; es ist weder ein Gutachten noch eine Rechts- oder Steuerberatung und stuft das Eigentum an den Werken nicht ein. ${brand} strukturiert die Angaben; die zuständigen Fachleute prüfen, was in ihre Zuständigkeit fällt.`,
    fileSlug: "kontinuitaetsbericht",
  },
};

const hasText = (v) => (typeof v === "string" ? v.trim() !== "" : v != null && v !== false);
const hasDocs = (a) => !(a?.documents == null || a.documents === "" || a.documents === "[]");

// Mêmes contrôles que la vue d'audit de la page de collection (libellés français ; le PDF traduit via T.checks).
export const CONTINUITY_CHECKS = [
  { key: "status", label: "Statut patrimonial non renseigné ou à clarifier", test: (a) => !a.heritage_status || a.heritage_status === "to_clarify" },
  { key: "owner", label: "Propriétaire à vérifier", test: (a) => a.ownership_verification === "to_verify" },
  { key: "provenance", label: "Provenance non renseignée", test: (a) => !hasText(a.provenance) },
  { key: "proof", label: "Aucune pièce justificative (facture ou document)", test: (a) => a.invoice !== true && !hasDocs(a) },
];

// collections : [{ id, title, nature, context, artworks: [{ id, title, artist, heritage_status, ownership_verification,
//   provenance, invoice, documents }] }]
// events : [{ artwork_title, event_type, title, event_date_start }]
// grants : [{ collection_title, grantee_name, level }]  ·  shares : [{ label, kind, expires_at }]
export function generateContinuityReport({ ownerName = "", collections = [], events = [], grants = [], shares = [], lang = "fr" } = {}) {
  const lg = pickLang(lang);
  const L = T[lg];
  const loc = LOCALE[lg];
  const fmtDate = (d) => {
    if (!d) return "—";
    const t = new Date(d);
    return Number.isNaN(t.getTime()) ? "—" : t.toLocaleDateString(loc);
  };

  const doc = new jsPDF("p", "mm", "a4");
  const M = 16;
  let y = 18;

  const all = collections.flatMap((c) => c.artworks.map((a) => ({ ...a, _collection: c.title })));
  const docsCount = all.filter(hasDocs).length;
  const audit = CONTINUITY_CHECKS.map((c) => ({ ...c, label: L.checks[c.key] || c.label, items: all.filter(c.test) }));
  const totalPoints = audit.reduce((s, c) => s + c.items.length, 0);

  const sectionTitle = (label) => {
    if (y > 250) { doc.addPage(); y = 18; }
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(25);
    doc.text(label, M, y);
    y += 3;
  };
  const afterTable = (gap = 8) => { y = (doc.lastAutoTable?.finalY || y) + gap; };

  // ---- En-tête ----
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(150);
  doc.text(L.title, M, y);
  doc.setFontSize(18); doc.setTextColor(20);
  y += 9; doc.text(doc.splitTextToSize(ownerName || L.fileFallback, 180), M, y);
  doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(90);
  y += 6; doc.text(L.issuedOn(new Date().toLocaleDateString(loc)), M, y);
  y += 6;

  // ---- 1. Synthèse ----
  autoTable(doc, {
    startY: y, theme: "plain", margin: { left: M, right: M },
    styles: { fontSize: 10, cellPadding: 1.6 },
    columnStyles: { 0: { fontStyle: "bold", textColor: 120, cellWidth: 80 } },
    body: [
      [L.summary[0], String(collections.length)],
      [L.summary[1], String(all.length)],
      [L.summary[2], String(docsCount)],
      [L.summary[3], `${all.filter((a) => a.ownership_verification !== "to_verify").length} / ${all.length}`],
      [L.summary[4], String(totalPoints)],
    ],
  });
  afterTable(8);

  // ---- 2. Ensembles et nature ----
  sectionTitle(L.s2);
  autoTable(doc, {
    startY: y, margin: { left: M, right: M },
    head: [L.s2Head],
    body: collections.map((c) => {
      const counts = {};
      c.artworks.forEach((a) => { const k = L.heritage[a.heritage_status] || L.notQualified; counts[k] = (counts[k] || 0) + 1; });
      return [
        c.title || L.untitled,
        L.nature[c.nature] || L.notSpecifiedF,
        L.context[c.context] || L.notSpecifiedM,
        String(c.artworks.length),
        Object.entries(counts).map(([k, n]) => `${n} ${k.toLowerCase()}`).join(", ") || "—",
      ];
    }),
    headStyles: { fillColor: [26, 26, 26], fontSize: 8.5 },
    styles: { fontSize: 8.5, cellPadding: 1.6 },
    columnStyles: { 3: { halign: "right" } },
  });
  afterTable(3);
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
  doc.text(L.s2Note, M, y + 3, { maxWidth: 178 });
  y += 10;

  // ---- 3. Points à clarifier ----
  sectionTitle(L.s3);
  autoTable(doc, {
    startY: y, margin: { left: M, right: M },
    head: [L.s3Head],
    body: audit.map((c) => [
      c.label,
      `${c.items.length} / ${all.length}`,
      c.items.slice(0, 3).map((a) => a.title || L.untitled).join(" ; ") + (c.items.length > 3 ? " …" : "") || "—",
    ]),
    headStyles: { fillColor: [26, 26, 26], fontSize: 8.5 },
    styles: { fontSize: 8.5, cellPadding: 1.6 },
    columnStyles: { 1: { halign: "right", cellWidth: 32 } },
  });
  afterTable(8);

  // ---- 4. Chronologie : expositions et mouvements ----
  sectionTitle(L.s4);
  autoTable(doc, {
    startY: y, margin: { left: M, right: M },
    head: [L.s4Head],
    body: events.length
      ? events.map((e) => [fmtDate(e.event_date_start), e.artwork_title || "—", L.event[e.event_type] || e.event_type, e.title || "—"])
      : [["—", "—", L.noEvent, "—"]],
    headStyles: { fillColor: [26, 26, 26], fontSize: 8.5 },
    styles: { fontSize: 8.5, cellPadding: 1.6 },
  });
  afterTable(8);

  // ---- 5. Accès accordés ----
  sectionTitle(L.s5);
  const accessRows = [
    ...grants.map((g) => [g.grantee_name || L.user, L.level[g.level] || g.level, g.collection_title || "—", L.noLimit]),
    ...shares.map((s) => [s.label || L.shareLink, L.readLink, L.sharedDossier, fmtDate(s.expires_at)]),
  ];
  autoTable(doc, {
    startY: y, margin: { left: M, right: M },
    head: [L.s5Head],
    body: accessRows.length ? accessRows : [[L.noAccess, "—", "—", "—"]],
    headStyles: { fillColor: [26, 26, 26], fontSize: 8.5 },
    styles: { fontSize: 8.5, cellPadding: 1.6 },
  });
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
  y = (doc.lastAutoTable?.finalY || y) + 3;
  doc.text(L.s5Note, M, y + 3);
  y += 10;

  // ---- 6. Démarches à prévoir ----
  sectionTitle(L.s6);
  const steps = [];
  const byKey = Object.fromEntries(audit.map((c) => [c.key, c.items.length]));
  ["status", "owner", "provenance", "proof"].forEach((k) => { if (byKey[k]) steps.push(L.steps[k](byKey[k])); });
  if (!steps.length) steps.push(L.steps.none);
  steps.forEach((s, i) => {
    if (y > 270) { doc.addPage(); y = 18; }
    doc.setFont("helvetica", "normal"); doc.setFontSize(9.5); doc.setTextColor(40);
    const lines = doc.splitTextToSize(`${i + 1}. ${s}`, 178);
    doc.text(lines, M, y + 4);
    y += lines.length * 4.6 + 1;
  });

  // ---- Pied : cadrage ----
  if (y > 262) { doc.addPage(); }
  doc.setFont("helvetica", "italic"); doc.setFontSize(7.5); doc.setTextColor(140);
  doc.text(doc.splitTextToSize(L.foot(BRAND_UPPER, new Date().toLocaleDateString(loc)), 180), M, 281);

  const slug = (ownerName || "dossier").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);
  doc.save(`${L.fileSlug}-${slug || "dossier"}.pdf`);
}
